import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Btn, Input } from "@/components/ui-kit";
import { useLang } from "@/contexts/LanguageContext";
import { Mail, ArrowLeft, KeyRound } from "lucide-react";

export function ForgotPassword() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [resetToken, setResetToken] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(""); setResetToken("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMsg("If this email exists, a reset link has been generated. (Demo: token shown below)");
      if (data.reset_token) setResetToken(data.reset_token);
    } catch (e) {
      setMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6 dark:bg-slate-900">
      <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-md shadow-sm border border-slate-200 dark:border-slate-700 p-8">
        <div className="w-12 h-12 rounded-md bg-blue-50 dark:bg-blue-900/40 grid place-items-center mb-6">
          <KeyRound className="w-6 h-6 text-blue-700 dark:text-blue-400"/>
        </div>
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white mb-2" style={{ fontFamily: "Manrope" }}>Forgot password?</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">Enter your email and we'll send you a reset link.</p>
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <Input label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email"/>
          <Btn type="submit" className="w-full" disabled={busy} data-testid="forgot-submit">{busy ? t("loading") : "Send reset link"}</Btn>
        </form>
        {msg && (
          <div className="mt-4 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm">
            {msg}
            {resetToken && (
              <Link to={`/reset-password?token=${resetToken}`} className="block mt-2 text-blue-700 underline font-mono text-xs break-all">
                Click here to reset →
              </Link>
            )}
          </div>
        )}
        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-blue-700 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3"/>Back to login</Link>
        </div>
      </div>
    </div>
  );
}

export function ResetPassword() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== pwd2) { setErr("Passwords don't match"); return; }
    if (pwd.length < 6) { setErr("Password must be at least 6 characters"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 dark:bg-slate-900 p-6">
      <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-md shadow-sm border border-slate-200 dark:border-slate-700 p-8">
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white mb-2" style={{ fontFamily: "Manrope" }}>Reset password</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">Choose a new password.</p>
        {done ? (
          <div className="p-4 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm">
            Password reset successfully. Redirecting to login...
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <Input label="New Password" type="password" required minLength={6} value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="reset-pwd"/>
            <Input label="Confirm Password" type="password" required minLength={6} value={pwd2} onChange={(e) => setPwd2(e.target.value)} data-testid="reset-pwd2"/>
            {err && <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{err}</div>}
            <Btn type="submit" className="w-full" disabled={busy || !token} data-testid="reset-submit">{busy ? t("loading") : "Reset password"}</Btn>
          </form>
        )}
      </div>
    </div>
  );
}
